import { useEffect, useMemo, useRef } from "react";
import { useStyles } from "./RCADivisionsDropdown.styles";
import NewMainSelect from "components/NewMainSelect/NewMainSelect.view";
import { useTranslation } from "react-i18next";
import { LocationsEntity } from "entities/Locations/LocationsEntity";
import { IOption } from "components/NewMainSelect/NewMainSelectInterfaces";
import { IRCADivisionsDropdown } from "./RCADivisionsDropdownInterfaces";

const RCADivisionsDropdown = ({
    value,
    onChange,
    handleOnComponentMount,
    multiple,
    className,
    fullWidth,
    placeholder,
    disabled,
    loadingDefaults,
}: IRCADivisionsDropdown) => {
    const { locationOptions } = LocationsEntity.GetRCAComplianceLocationOptions();
    const classes = useStyles();
    const { t } = useTranslation();

    const options = useMemo(
        () =>
            (locationOptions.division ?? []).map((division) => ({
                id: division.division_id,
                name: division.name,
            })) as IOption[],
        [locationOptions.division],
    );

    const prevOptionsKeyRef = useRef<string | null>(null);
    useEffect(() => {
        if (!multiple || options.length === 0 || loadingDefaults) return;
        const optionsKey = options.map((o) => o.id).join(",");
        if (optionsKey === prevOptionsKeyRef.current) return;

        const isFirstFire = prevOptionsKeyRef.current === null;
        if (handleOnComponentMount) handleOnComponentMount(options.map((option) => option.id), isFirstFire);
        prevOptionsKeyRef.current = optionsKey;
    }, [multiple, handleOnComponentMount, options, loadingDefaults]);

    if (multiple) {
        return (
            <NewMainSelect
                fullWidth={false}
                disableMenuPropsStyles
                disabled={disabled}
                multiple
                className={`${classes.select} ${className || ""} ${fullWidth ? classes.fullWidth : ""}`}
                options={options}
                value={value}
                onChange={(selected) => {
                    onChange(selected);
                }}
                placeholder={placeholder || t("data.select_valid_option")}
                withSearchBar
            />
        );
    }

    return (
        <NewMainSelect
            fullWidth={false}
            disableMenuPropsStyles
            disabled={disabled}
            multiple={false}
            withSearchBar
            className={`${classes.select} ${className || ""} ${fullWidth ? classes.fullWidth : ""}`}
            options={options}
            value={value}
            onChange={(selected) => {
                onChange(selected);
            }}
            placeholder={placeholder || t("corporate_dashboard.filters.all")}
        />
    );
};

export default RCADivisionsDropdown;
